// @catalog F.010
/**
 * SelectionBar — banda fija debajo del ForecastToolbar (N selecciones + estimado + 4 acciones).
 *
 * Decisión 2026-05-22 (usuario): se movió de sticky bottom a banda superior in-flow,
 * junto a los controles de selección masiva del ForecastToolbar — los controles
 * que crean la selección viven arriba; las acciones sobre esa selección deben
 * vivir al lado, no al borde opuesto de la pantalla.
 *
 * Comportamiento:
 *   - Cuando selectedIds.size === 0 → return null (NO renderizar — no quita altura).
 *   - Cuando > 0 → bloque in-flow ancho completo, bg ink-1 + color #fff, slide-in 220ms
 *     desde arriba (translateY(-100%) → 0).
 *   - "Calcular forecast" verde primary (var(--accent)).
 *   - "Exportar" (placeholder hasta feature dedicada).
 *   - Menú "Más acciones" (MoreVert) contiene Activar / Desactivar compras —
 *     decisión 2026-05-22 (usuario): los botones directos eran fáciles de pulsar
 *     por accidente. Pasarlos a menú añade un click intencional sin perder funcionalidad.
 *   - "X" clear selection (onClear).
 *
 * Estimado (formato MXN compact via Intl.NumberFormat es-MX):
 *   sum(forecastNextPeriod * (lastPurchaseCost ?? 0)) sobre productos seleccionados.
 *   Cuando lastPurchaseCost ausente → suma conservadora; backend lo plumbea (feature 11).
 */
import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  GlobalStyles,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from "@mui/icons-material";
import type { ProductData } from "./types";

export interface SelectionBarProps {
  selectedIds: Set<string>;
  products: ProductData[];
  onCalcForecast: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  onExport: () => void;
  onClear: () => void;
}

const mxnCompact = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  notation: "compact",
  maximumFractionDigits: 2,
});

function computeEstimated(
  selectedIds: Set<string>,
  products: ProductData[],
): number {
  if (selectedIds.size === 0) return 0;
  let total = 0;
  for (const p of products) {
    if (!selectedIds.has(p.unifiedProductId)) continue;
    const qty = p.forecastNextPeriod ?? 0;
    const cost = p.lastPurchaseCost ?? 0;
    total += qty * cost;
  }
  return total;
}

const SELBAR_KEYFRAMES = (
  <GlobalStyles
    styles={{
      "@keyframes fc-selbar-in": {
        from: { transform: "translateY(-100%)" },
        to: { transform: "translateY(0)" },
      },
    }}
  />
);

export function SelectionBar(props: SelectionBarProps): React.ReactElement | null {
  const {
    selectedIds,
    products,
    onCalcForecast,
    onActivate,
    onDeactivate,
    onExport,
    onClear,
  } = props;

  const count = selectedIds.size;
  const estimated = useMemo(
    () => computeEstimated(selectedIds, products),
    [selectedIds, products],
  );

  const [moreAnchorEl, setMoreAnchorEl] = useState<HTMLElement | null>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  const closeMoreMenu = () => setMoreAnchorEl(null);

  if (count === 0) return null;

  return (
    <>
      {SELBAR_KEYFRAMES}
      <Box
        data-testid="forecast-selection-bar"
        sx={{
          bgcolor: "var(--ink-1)",
          color: "#fff",
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          flexShrink: 0,
          borderBottom: "1px solid var(--border)",
          animation: "fc-selbar-in 0.22s cubic-bezier(0.32, 0.72, 0.24, 1)",
        }}
      >
        <Stack direction="row" alignItems="center" gap="14px" sx={{ fontSize: 13 }}>
          <Box
            component="span"
            data-testid="forecast-selection-bar-count"
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.12)",
              padding: "4px 10px",
              borderRadius: "6px",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {count} seleccionado{count === 1 ? "" : "s"}
          </Box>
          <Box
            component="span"
            data-testid="forecast-selection-bar-estimated"
            sx={{ color: "#cbd1de", fontSize: 12.5 }}
          >
            Estimado:{" "}
            <Box component="strong" sx={{ color: "#fff", fontWeight: 600 }}>
              {mxnCompact.format(estimated)}
            </Box>
          </Box>
        </Stack>

        <Stack
          direction="row"
          gap="8px"
          alignItems="center"
          sx={{ ml: "auto" }}
        >
          <SelbarButton
            variant="primary"
            onClick={onCalcForecast}
            testId="forecast-selection-bar-calc"
            icon={<TrendingUpIcon sx={{ fontSize: 13 }} />}
          >
            Calcular forecast
          </SelbarButton>
          <SelbarButton
            onClick={onExport}
            testId="forecast-selection-bar-export"
            icon={<DownloadIcon sx={{ fontSize: 13 }} />}
          >
            Exportar
          </SelbarButton>
          <Box
            component="button"
            type="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
              setMoreAnchorEl(e.currentTarget)
            }
            data-testid="forecast-selection-bar-more"
            aria-label="Más acciones"
            aria-haspopup="menu"
            aria-expanded={moreMenuOpen}
            title="Más acciones"
            sx={{
              width: 32,
              height: 32,
              borderRadius: "6px",
              display: "grid",
              placeItems: "center",
              color: "#cbd1de",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              border: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.18)",
                color: "#fff",
              },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </Box>
          <Menu
            anchorEl={moreAnchorEl}
            open={moreMenuOpen}
            onClose={closeMoreMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            keepMounted
          >
            <MenuItem
              onClick={() => {
                closeMoreMenu();
                onActivate();
              }}
              data-testid="forecast-selection-bar-activate"
            >
              <ListItemIcon>
                <ToggleOnIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Activar compras</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMoreMenu();
                onDeactivate();
              }}
              data-testid="forecast-selection-bar-deactivate"
            >
              <ListItemIcon>
                <ToggleOffIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Desactivar compras</ListItemText>
            </MenuItem>
          </Menu>
          <Box
            component="button"
            type="button"
            onClick={onClear}
            data-testid="forecast-selection-bar-clear"
            title="Limpiar selección"
            aria-label="Limpiar selección"
            sx={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              display: "grid",
              placeItems: "center",
              color: "#9aa0ad",
              bgcolor: "transparent",
              border: 0,
              cursor: "pointer",
              fontFamily: "inherit",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
                color: "#fff",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </Box>
        </Stack>
      </Box>
    </>
  );
}

interface SelbarButtonProps {
  variant?: "primary" | "danger";
  onClick: () => void;
  testId?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

function SelbarButton(props: SelbarButtonProps): React.ReactElement {
  const { variant, onClick, testId, icon, children } = props;
  const isPrimary = variant === "primary";
  const isDanger = variant === "danger";
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      data-testid={testId}
      sx={{
        padding: "7px 12px",
        borderRadius: "6px",
        fontSize: 13,
        fontWeight: 500,
        bgcolor: isPrimary ? "var(--accent)" : "rgba(255, 255, 255, 0.1)",
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        border: 0,
        cursor: "pointer",
        fontFamily: "inherit",
        "&:hover": {
          bgcolor: isPrimary
            ? "var(--accent-2)"
            : isDanger
              ? "var(--neg)"
              : "rgba(255, 255, 255, 0.18)",
        },
      }}
    >
      {icon}
      {children}
    </Box>
  );
}
